import { AvailableLocale } from '@models/Lang'
import { QRCode, Typography } from 'antd'
import { CSSProperties } from 'react'
import {
  FieldHighlightProps,
  ResolvedWalletCard,
  WalletCardAsset,
} from '../types'
import { WALLET_LABELS } from '../labels'
import { useWalletAssetUrl } from '../useWalletAssetUrl'
import WalletFieldZone from '../WalletFieldZone'

const { Text } = Typography

const CARD_WIDTH = 340

const heroPlaceholder = (foregroundColor: string): CSSProperties => ({
  backgroundImage: `repeating-linear-gradient(135deg, ${foregroundColor}1a 0 10px, transparent 10px 20px)`,
})

/**
 * Google Wallet has NO foreground/label color field — GenericObject only
 * carries hexBackgroundColor, and Google auto-picks white or a near-black for
 * text based on the background's contrast. This mirrors that, so the preview is
 * honest: the configured foreground/label colors reach only Apple and the
 * in-app card, never Android.
 */
const contrastText = (hex: string): string => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16))
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255

  return luminance > 0.6 ? '#202124' : '#ffffff'
}

interface Props extends FieldHighlightProps {
  card: ResolvedWalletCard
  logo: WalletCardAsset
  hero: WalletCardAsset
  /** The back-office language selector's current locale, for label translation. */
  locale?: AvailableLocale
}

/**
 * Mirrors Google Wallet's generic-card layout: hexBackgroundColor fill, logo
 * + cardTitle row, header as the big line, the two text modules as a
 * two-item row, hero image band, then the barcode at the bottom with its value
 * printed beneath (as Google Wallet renders it).
 *
 * GooglePassBuilder::buildObject sets the object's `cardTitle` field to
 * brandLine and its `header` field to cardTitle (yes, swapped-sounding, but
 * that's the actual mapping — brandLine is the small chrome text next to the
 * logo, cardTitle is the big line), so this face follows the same mapping.
 */
const GooglePassFace = ({
  card,
  logo,
  hero,
  locale = 'en',
  activeField,
  onActiveFieldChange,
}: Props) => {
  const logoUrl = useWalletAssetUrl(logo)
  const heroUrl = useWalletAssetUrl(hero)
  const highlight = { activeField, onActiveFieldChange }
  const labels = WALLET_LABELS[locale]
  // Google decides text color itself — the configured foreground/label are
  // ignored on Android.
  const autoText = contrastText(card.backgroundColor)

  const labelStyle: CSSProperties = {
    color: autoText,
    fontSize: 11,
    fontWeight: 500,
  }
  const valueStyle: CSSProperties = {
    color: autoText,
    fontSize: 14,
    fontWeight: 600,
  }

  return (
    <WalletFieldZone
      field="backgroundColor"
      radius={12}
      {...highlight}
      style={{
        width: CARD_WIDTH,
        overflow: 'hidden',
        backgroundColor: card.backgroundColor,
        boxShadow: '0 12px 28px rgba(0, 0, 0, 0.22)',
        fontFamily: '"Google Sans", Roboto, Arial, sans-serif',
      }}
    >
      <div style={{ padding: '16px 16px 4px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {logoUrl ? (
            <WalletFieldZone field="logo" radius={12} {...highlight}>
              <img
                src={logoUrl}
                alt="Logo"
                style={{
                  height: 24,
                  width: 24,
                  borderRadius: 6,
                  objectFit: 'contain',
                  background: '#fff',
                  display: 'block',
                }}
              />
            </WalletFieldZone>
          ) : (
            <WalletFieldZone field="logo" radius={8} {...highlight}>
              <div
                style={{
                  height: 24,
                  width: 24,
                  borderRadius: 6,
                  background: `${autoText}26`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: autoText,
                  fontSize: 11,
                  fontWeight: 700,
                }}
              >
                {card.brandLine.charAt(0)}
              </div>
            </WalletFieldZone>
          )}
          <WalletFieldZone field="brandLine" {...highlight}>
            <Text style={{ color: autoText, fontSize: 12 }}>
              {card.brandLine}
            </Text>
          </WalletFieldZone>
        </div>

        <WalletFieldZone
          field="cardTitle"
          {...highlight}
          style={{ marginTop: 8 }}
        >
          <Text
            style={{
              display: 'block',
              color: autoText,
              fontSize: 20,
              fontWeight: 700,
            }}
          >
            {card.cardTitle}
          </Text>
        </WalletFieldZone>
      </div>

      <div style={{ display: 'flex', padding: '12px 16px', gap: 16 }}>
        <div
          style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {/* No foreground/label zones here: Google ignores those colors and
              auto-picks the text color, so they aren't linkable on this face. */}
          <span style={labelStyle}>{labels.member}</span>
          <span style={valueStyle}>{card.member.name}</span>
        </div>
        <div
          style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <span style={labelStyle}>{labels.cardNumber}</span>
          <span style={valueStyle}>{card.member.cardNumber}</span>
        </div>
      </div>

      {card.showExpiry && (
        <WalletFieldZone
          field="showExpiry"
          {...highlight}
          style={{ margin: '0 16px 12px' }}
        >
          <span style={labelStyle}>{labels.expires}</span>{' '}
          <span style={valueStyle}>{card.member.expiryDate}</span>
        </WalletFieldZone>
      )}

      <WalletFieldZone field="hero" radius={0} {...highlight}>
        <div
          style={{
            height: 100,
            ...(heroUrl
              ? {
                  backgroundImage: `url(${heroUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : heroPlaceholder(autoText)),
          }}
        />
      </WalletFieldZone>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 6,
          padding: 16,
        }}
      >
        <div style={{ background: '#fff', borderRadius: 12, padding: 10 }}>
          <QRCode
            value={`plethore:member:${card.member.cardNumber}`}
            size={120}
            bordered={false}
            color="#000"
            bgColor="#fff"
          />
        </div>
        <Text
          style={{
            color: autoText,
            fontSize: 11,
            letterSpacing: 0.4,
          }}
        >
          {card.member.cardNumber}
        </Text>
      </div>
    </WalletFieldZone>
  )
}

export default GooglePassFace
